import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useShallow } from 'zustand/react/shallow';
import { type Bonus, BonusStage, type Freespin } from '@/api/common';
import { useGetCommonStore } from '@/store';
import {
  getCurrentLevelIndex,
  getMissionLevels,
  type ILevelConfig,
} from '../helpers';

const getRewardGroupKeys = (bonuses: Bonus[], freespins: Freespin[]) => {
  const canceled = new Set<string | undefined>();
  const issued = new Set<string | undefined>();

  for (const { stage, groupKey } of [...bonuses, ...freespins]) {
    if (stage === BonusStage.Canceled) canceled.add(groupKey);
    else if (stage === BonusStage.Issued) issued.add(groupKey);
  }

  return { canceled, issued };
};

export const useMissionLevels = (
  levelsConfig: ILevelConfig[],
  groupPrefix?: string,
  useFullNameAsGroupId = false
) => {
  const { i18n } = useTranslation();

  const useCommonStore = useGetCommonStore();

  const { user, bonuses, freespins, bonusesPending, freespinsPending } =
    useCommonStore(
      useShallow((state) => ({
        user: state.user,
        bonuses: state.bonuses,
        freespins: state.freespins,
        bonusesPending: state.bonusesPending,
        freespinsPending: state.freespinsPending,
      }))
    );
  const isBonusDataPending = bonusesPending || freespinsPending;

  const rewardGroupKeys = useMemo(
    () => getRewardGroupKeys(bonuses, freespins),
    [bonuses, freespins]
  );

  const levels = useMemo(
    () =>
      getMissionLevels(
        groupPrefix,
        useFullNameAsGroupId,
        levelsConfig,
        user,
        rewardGroupKeys,
        isBonusDataPending,
        user?.currency,
        i18n.language
      ),
    [
      groupPrefix,
      useFullNameAsGroupId,
      levelsConfig,
      user,
      rewardGroupKeys,
      isBonusDataPending,
      i18n.language,
    ]
  );
  const currentLevelIndex = useMemo(
    () => getCurrentLevelIndex(levels),
    [levels]
  );

  return { levels, currentLevelIndex };
};
